import { apiClient } from '@/lib/apiClient';
import type { QuickActionCategory } from '@hector/shared-types/quick-actions';

export interface UserPrompt {
  id: string;
  title: string;
  description: string | null;
  prompt_content: string;
  sort_order: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  /**
   * Colonne du panneau Assistant (migration 175). Optionnel : un backend
   * antérieur ne l'envoie pas — lire via `promptCategory()`, qui vaut
   * 'analyse' par défaut.
   */
  category?: QuickActionCategory;
  /** Clé d'une action native Hector : masquable et réordonnable, jamais modifiable. */
  native_key?: string | null;
  /** Native masquée par l'avocat via « Gérer ». */
  is_hidden?: boolean;
}

export interface CreateUserPromptInput {
  title: string;
  description?: string;
  prompt_content: string;
  category?: QuickActionCategory;
}

export function promptCategory(prompt: UserPrompt): QuickActionCategory {
  return prompt.category ?? 'analyse';
}

export function isNativePrompt(prompt: UserPrompt): boolean {
  return Boolean(prompt.native_key);
}

interface UserPromptsResponse {
  success: boolean;
  prompts: UserPrompt[];
}

interface SinglePromptResponse {
  success: boolean;
  prompt: UserPrompt;
}

const BASE = '/user-prompts';
// Sans ce paramètre, le serveur ne renvoie que la liste Analyse : c'est ce
// qui protège les clients antérieurs aux colonnes (voir user-prompts.routes.ts).
const WITH_CATEGORIES = 'include=categories';

export const userPromptsApi = {
  list: async (): Promise<UserPrompt[]> => {
    const res = await apiClient.get<UserPromptsResponse>(`${BASE}?${WITH_CATEGORIES}`);
    return res.prompts;
  },

  create: async (data: CreateUserPromptInput): Promise<UserPrompt> => {
    const res = await apiClient.post<SinglePromptResponse>(BASE, data);
    return res.prompt;
  },

  update: async (
    id: string,
    data: { title?: string; description?: string; prompt_content?: string },
  ): Promise<UserPrompt> => {
    const res = await apiClient.put<SinglePromptResponse>(`${BASE}/${id}`, data);
    return res.prompt;
  },

  /** Masque ou réaffiche une native (les autres actions se suppriment). */
  setHidden: async (id: string, hidden: boolean): Promise<UserPrompt> => {
    const res = await apiClient.put<SinglePromptResponse>(`${BASE}/${id}`, { is_hidden: hidden });
    return res.prompt;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /** Toute la bibliothèque, ou tous les ids d'une seule colonne (natives masquées comprises). */
  reorder: async (items: { id: string; sort_order: number }[]): Promise<UserPrompt[]> => {
    const res = await apiClient.patch<UserPromptsResponse>(`${BASE}/reorder?${WITH_CATEGORIES}`, { items });
    return res.prompts;
  },
};
